import { useEffect, useRef } from "react";

const TURNSTILE_SITE_KEY = "0x4AAAAAACjU3-2918AA2-lc";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/**
 * Cloudflare Turnstile widget (managed mode).
 * Calls onVerify(token) when verification succeeds, onExpire() when token expires.
 * Parent must include the token in form submission; backend must verify via siteverify API.
 */
export function TurnstileWidget({ onVerify, onExpire, theme = "light" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!containerRef.current) return;

    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current) return;
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme,
          callback: (token) => onVerifyRef.current?.(token),
          "expired-callback": () => {
            onVerifyRef.current?.("");
            onExpireRef.current?.();
          },
          "error-callback": () => {
            onVerifyRef.current?.("");
          },
        });
      } catch (e) {
        console.error("Turnstile render error:", e);
      }
    };

    if (window.turnstile) {
      renderWidget();
      return () => {
        if (widgetIdRef.current != null && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (_) {}
        }
      };
    }

    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = renderWidget;
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {}
      }
    };
  }, [theme]);

  return <div ref={containerRef} data-testid="turnstile-widget" />;
}

export default TurnstileWidget;
